import type { NflTiebreakerTeam, TiebreakerResult, GameResultsLookup, TiebreakerOptions, TiebreakerStepFn, StepContext } from '../../shared/tiebreaker/types';
import type { TiebreakerRuleSetSnapshot } from '../../shared/league-structure/types';
import { createContext } from '../../shared/tiebreaker/step-runner';
import { groupByDescending, worstOf, computeHthRecords, hthWinPct, calcPctNaN, addExplanation } from '../../shared/tiebreaker/helpers';
import { RankMethod } from '../../shared/enums/rank-method';
import { NFL_DIVISION_STEPS, NFL_CONFERENCE_STEPS, resolveNflSteps } from './nfl-steps';

/**
 * NFL tiebreakers (h2h, conf%, common, sov, sos) only apply between teams TIED IN
 * OVERALL RECORD. Higher-record teams always rank above lower-record teams without
 * tiebreakers being consulted. This helper enforces that contract by grouping by
 * overall record first and only running the supplied step list within each tied group.
 */
function rankByOverallRecordThenSteps(
  teams: NflTiebreakerTeam[],
  steps: TiebreakerStepFn<NflTiebreakerTeam>[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam[] {
  const ordered: NflTiebreakerTeam[] = [];
  const pctGroups = groupByDescending(teams, t => t.records.all.pctNaN);
  for (const group of pctGroups) {
    if (group.length === 1) {
      ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.AllPCT);
      ordered.push(group[0]);
    } else {
      const sub = runNflTiebreakerSteps(group, steps, ctx);
      ordered.push(...sub.orderedTeams);
    }
  }
  return ordered;
}

function runNflTiebreakerSteps(
  teams: NflTiebreakerTeam[],
  steps: TiebreakerStepFn<NflTiebreakerTeam>[],
  ctx: StepContext<NflTiebreakerTeam>,
): TiebreakerResult<NflTiebreakerTeam> {
  const ordered: NflTiebreakerTeam[] = [];
  let remaining = [...teams];
  const deferredGroups: NflTiebreakerTeam[][] = [];
  const maxIterations = teams.length * steps.length + teams.length;
  let iterations = 0;

  while (remaining.length > 0 || deferredGroups.length > 0) {
    if (remaining.length === 0) {
      remaining = deferredGroups.shift() ?? [];
      continue;
    }

    if (iterations++ > maxIterations) {
      coinFlipRemaining(remaining, ordered, ctx, 'Cycle protection: coin flip.');
      remaining = [];
      continue;
    }

    if (remaining.length === 1) {
      ordered.push(remaining[0]);
      remaining = [];
      continue;
    }

    let resolved = false;
    let continuingTopSubgroup = false;

    for (const step of steps) {
      const result = step(remaining, ctx);

      if (result.winners.length === 0 && result.remaining.length === remaining.length) {
        continue;
      }

      if (result.winners.length > 0 && result.remaining.length === 0) {
        appendWinners(result.winners, ordered, ctx, result.rankMethod);
        remaining = [];
        resolved = true;
        break;
      }

      if (result.winners.length > 0 && result.remaining.length > 0) {
        if (result.winners.length > 1) {
          deferredGroups.unshift(result.remaining);
          remaining = result.winners;
          resolved = true;
          continuingTopSubgroup = true;
          break;
        }

        appendWinners(result.winners, ordered, ctx, result.rankMethod);
        remaining = result.remaining;
        resolved = true;
        break;
      }
    }

    if (continuingTopSubgroup) {
      continue;
    }

    if (!resolved && remaining.length > 0) {
      coinFlipRemaining(remaining, ordered, ctx, 'All tiebreaker steps exhausted. Coin flip.');
      remaining = [];
    }
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations, explainerDetails: ctx.explainerDetails };
}

function runNflSingleWinnerSteps(
  teams: NflTiebreakerTeam[],
  steps: TiebreakerStepFn<NflTiebreakerTeam>[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  return runNflTiebreakerSteps(teams, steps, ctx).orderedTeams[0];
}

function appendWinners(
  winners: NflTiebreakerTeam[],
  ordered: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
  rankMethod: RankMethod | undefined,
): void {
  for (const winner of winners) {
    if (rankMethod !== undefined) {
      ctx.rankMethods.set(winner.teamAbbreviation, rankMethod);
    }
    ordered.push(winner);
  }
}

function coinFlipRemaining(
  remainingTeams: NflTiebreakerTeam[],
  ordered: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
  explanation: string,
): void {
  let remaining = remainingTeams;
  while (remaining.length > 0) {
    const winner = ctx.coinFlipFn(remaining);
    ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
    addExplanation(ctx, winner, explanation);
    ordered.push(winner);
    remaining = remaining.filter(team => team !== winner);
  }
}

export interface NflTiebreakerOptions extends TiebreakerOptions {
  /** When true, use the existing common-games methodology variant */
  commonGamesMethodology?: 'standard' | 'legacy';
  tiebreakerRuleSet?: TiebreakerRuleSetSnapshot | null;
}

function getNflDivisionSteps(
  options?: NflTiebreakerOptions,
): TiebreakerStepFn<NflTiebreakerTeam>[] {
  return resolveNflSteps(
    options?.tiebreakerRuleSet?.paths?.divisional,
    NFL_DIVISION_STEPS,
  );
}

function getNflWildcardSteps(
  options?: NflTiebreakerOptions,
): TiebreakerStepFn<NflTiebreakerTeam>[] {
  return resolveNflSteps(
    options?.tiebreakerRuleSet?.paths?.wildcard,
    NFL_CONFERENCE_STEPS,
  );
}

function getNflConferenceSteps(
  options?: NflTiebreakerOptions,
): TiebreakerStepFn<NflTiebreakerTeam>[] {
  return resolveNflSteps(
    options?.tiebreakerRuleSet?.paths?.conference,
    NFL_CONFERENCE_STEPS,
  );
}

/**
 * NFL Division tiebreaker: pick one winner from a group of same-division teams.
 */
export function nflDivisionTiebreaker(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NflTiebreakerOptions,
): NflTiebreakerTeam {
  const ctx = createContext<NflTiebreakerTeam>(games, options);
  return runNflSingleWinnerSteps(teams, getNflDivisionSteps(options), ctx);
}

/**
 * NFL Division ranker: order all teams in a division.
 * Groups by overall record first; tiebreaker steps only run within tied groups.
 */
export function nflDivisionRanker(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NflTiebreakerOptions,
): TiebreakerResult<NflTiebreakerTeam> {
  const ctx = createContext<NflTiebreakerTeam>(games, options);
  const ordered = rankByOverallRecordThenSteps(teams, getNflDivisionSteps(options), ctx);
  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations, explainerDetails: ctx.explainerDetails };
}

/**
 * NFL Wildcard ranker: order an already-filtered set of wildcard candidates.
 * Schedule-picker callers pass these slices after division winners have already
 * been removed, so applying the full conference-ranker promotion step would use
 * the wrong rule-set path.
 */
export function nflWildcardRanker(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NflTiebreakerOptions,
): TiebreakerResult<NflTiebreakerTeam> {
  const ctx = createContext<NflTiebreakerTeam>(games, options);
  const divisionSteps = getNflDivisionSteps(options);
  const wildcardSteps = getNflWildcardSteps(options);
  const ordered = rankWildcardCandidates(teams, divisionSteps, wildcardSteps, ctx);

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations, explainerDetails: ctx.explainerDetails };
}

function rankWildcardCandidates(
  teams: NflTiebreakerTeam[],
  divisionSteps: TiebreakerStepFn<NflTiebreakerTeam>[],
  wildcardSteps: TiebreakerStepFn<NflTiebreakerTeam>[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam[] {
  const ordered: NflTiebreakerTeam[] = [];
  let remaining = [...teams];

  while (remaining.length > 0) {
    const topRecordGroup = groupByDescending(remaining, t => t.records.all.pctNaN)[0];
    if (topRecordGroup.length === 1) {
      ctx.rankMethods.set(topRecordGroup[0].teamAbbreviation, RankMethod.AllPCT);
      ordered.push(topRecordGroup[0]);
      remaining = remaining.filter(team => team !== topRecordGroup[0]);
      continue;
    }

    const winner = runConferenceSingleWinner(topRecordGroup, divisionSteps, wildcardSteps, ctx);
    ordered.push(winner);
    remaining = remaining.filter(team => team !== winner);
  }

  return ordered;
}

/**
 * NFL Conference tiebreaker: pick one winner from teams that may span multiple divisions.
 * If all teams are from the same division, uses division steps instead.
 */
export function nflConfTiebreaker(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NflTiebreakerOptions,
): NflTiebreakerTeam {
  const ctx = createContext<NflTiebreakerTeam>(games, options);
  const divisionSteps = getNflDivisionSteps(options);
  const conferenceSteps = getNflConferenceSteps(options);
  return runConferenceSingleWinner(teams, divisionSteps, conferenceSteps, ctx);
}

export function nflConferencePickTop(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  isWildcard: boolean,
  options?: NflTiebreakerOptions,
): NflTiebreakerTeam {
  return nflConferencePickTopResult(teams, games, isWildcard, options).orderedTeams[0];
}

export function nflConferencePickTopResult(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  isWildcard: boolean,
  options?: NflTiebreakerOptions,
): TiebreakerResult<NflTiebreakerTeam> {
  const ctx = createContext<NflTiebreakerTeam>(games, options);
  const winner = runConferenceSingleWinner(
    teams,
    getNflDivisionSteps(options),
    isWildcard ? getNflWildcardSteps(options) : getNflConferenceSteps(options),
    ctx,
  );
  return { orderedTeams: [winner], rankMethods: ctx.rankMethods, explanations: ctx.explanations, explainerDetails: ctx.explainerDetails };
}

function runConferenceSingleWinner(
  teams: NflTiebreakerTeam[],
  divisionSteps: TiebreakerStepFn<NflTiebreakerTeam>[],
  finalSteps: TiebreakerStepFn<NflTiebreakerTeam>[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  // Check if all teams are from same division
  const allSameDiv = teams.every(t => t.division === teams[0].division);
  if (allSameDiv) {
    return runNflSingleWinnerSteps(teams, divisionSteps, ctx);
  }

  // Group by division to find same-div subgroups
  const divGroups = new Map<string, NflTiebreakerTeam[]>();
  for (const t of teams) {
    const group = divGroups.get(t.division) || [];
    group.push(t);
    divGroups.set(t.division, group);
  }

  // If any division has multiple teams, run division tiebreaker within that group first
  const representatives: NflTiebreakerTeam[] = [];
  for (const [, group] of divGroups) {
    if (group.length === 1) {
      representatives.push(group[0]);
    } else {
      // Pick one from each same-division subgroup using division steps
      const winner = runNflSingleWinnerSteps(group, divisionSteps, ctx);
      representatives.push(winner);
    }
  }

  // Now run conference steps on the representatives (one per division)
  if (representatives.length === 1) {
    return representatives[0];
  }
  return runNflSingleWinnerSteps(representatives, finalSteps, ctx);
}

/**
 * NFL Conference ranker: rank all teams in a conference for playoff seeding.
 * Division winners are promoted first, then wild card teams are ranked.
 *
 * At every level (per-division ranking, division-winner seeding, wildcard seeding),
 * teams are grouped by overall record before tiebreaker steps are applied. Per NFL
 * rules a 12-3 team is always seeded above a 10-5 team regardless of h2h, conf%, etc.
 */
export function nflConferenceRanker(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  divisions: string[],
  options?: NflTiebreakerOptions,
): TiebreakerResult<NflTiebreakerTeam> {
  const ctx = createContext<NflTiebreakerTeam>(games, options);
  const divisionSteps = getNflDivisionSteps(options);
  const wildcardSteps = getNflWildcardSteps(options);
  const conferenceSteps = getNflConferenceSteps(options);
  const ordered: NflTiebreakerTeam[] = [];

  // Step 1: Find division winners — within each division, group by overall record
  // first so a 10-5 team can never leapfrog a 12-3 team via h2h/conf%.
  const divWinners: NflTiebreakerTeam[] = [];
  const nonWinners: NflTiebreakerTeam[] = [];

  for (const div of divisions) {
    const divTeams = teams.filter(t => t.division === div);
    if (divTeams.length === 0) continue;
    const ranked = rankByOverallRecordThenSteps(divTeams, divisionSteps, ctx);
    divWinners.push(ranked[0]);
    nonWinners.push(...ranked.slice(1));
  }

  // Step 2: Rank division winners 1..N — overall record decides seeding before
  // any conference tiebreakers (h2h, conf%, common, sov, sos) are consulted.
  if (divWinners.length > 1) {
    ordered.push(...rankByOverallRecordThenSteps(divWinners, conferenceSteps, ctx));
  } else {
    ordered.push(...divWinners);
  }

  // Step 3: Rank wildcards — same overall-record-first rule.
  if (nonWinners.length > 1) {
    ordered.push(...rankWildcardCandidates(nonWinners, divisionSteps, wildcardSteps, ctx));
  } else {
    ordered.push(...nonWinners);
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations, explainerDetails: ctx.explainerDetails };
}

/**
 * NFL Draft order. The worst regular-season team picks first.
 *
 * The NFL Draft Order tiebreaker procedure (NFL Record and Fact Book) is
 * structure-aware and starts with strength of schedule — NOT head-to-head:
 *
 *   1. Strength of schedule. Lower SOS = faced weaker schedule = picks earlier.
 *   2. If still tied, branch on the conference / division relationship of the
 *      tied group:
 *        a. Same division: H2H → Div% → Common(4) → Conf% → SOV → SOS → coin flip,
 *           all "worst-of" semantics.
 *        b. Same conference, different divisions: pick the worst from each
 *           division first (using the same-division ladder), then resolve
 *           the division reps via H2H(conf) → Conf% → Common(4) → SOV → SOS
 *           → coin flip.
 *        c. Different conferences: pick worst-of-division reps where
 *           applicable, then H2H(conf) → Common(4) → SOV → coin flip.
 *
 * Each team's DraftOrderRankMethod is set to the step that placed *that team*
 * relative to the team picking immediately before/after it. Teams with a
 * unique overall record never had a tiebreaker fire and remain unlabeled.
 */
export function nflDraftOrder(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NflTiebreakerOptions,
): TiebreakerResult<NflTiebreakerTeam> {
  const ctx = createContext<NflTiebreakerTeam>(games, options);

  // Group by overall record. Best record first; we walk the groups in reverse
  // so worse-record teams pick earlier.
  const pctGroups = groupByDescending(teams, t => t.records.all.pctNaN);
  const ordered: NflTiebreakerTeam[] = [];

  for (let i = pctGroups.length - 1; i >= 0; i--) {
    const group = pctGroups[i];
    if (group.length === 1)
    {
      ordered.push(group[0]);
      continue;
    }
    // Repeatedly extract the worst-pick team until this record group is empty.
    let remaining = group.slice();
    while (remaining.length > 0)
    {
      if (remaining.length === 1)
      {
        ordered.push(remaining[0]);
        break;
      }
      const worst = pickWorstForDraft(remaining, ctx);
      ordered.push(worst);
      remaining = remaining.filter(t => t !== worst);
    }
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations, explainerDetails: ctx.explainerDetails };
}

/**
 * Find the WORST team in a tied draft-order group per NFL rules.
 * Sets ctx.rankMethods on the returned team (and on all teams when the
 * resolution is a coin flip — matching V1 DraftOrderCoinFlip semantics).
 */
function pickWorstForDraft(
  teams: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  // Step 1: strength of schedule (always first for draft order).
  const worstSos = worstOf(teams, t => t.sos);
  if (worstSos.length === 1)
  {
    ctx.rankMethods.set(worstSos[0].teamAbbreviation, RankMethod.SOS);
    return worstSos[0];
  }
  if (worstSos.length < teams.length)
  {
    return pickWorstForDraft(worstSos, ctx);
  }

  // SOS didn't break — branch on the conference / division relationship.
  const divKey = (t: NflTiebreakerTeam) => `${t.conference}|${t.division}`;
  const distinctDivs = new Set(teams.map(divKey));

  // If any division is over-represented in the tied group, reduce to one rep
  // (the worst) per division before applying the cross-divisional ladder.
  let reps: NflTiebreakerTeam[];
  if (distinctDivs.size < teams.length)
  {
    const divGroups = new Map<string, NflTiebreakerTeam[]>();
    for (const t of teams)
    {
      const k = divKey(t);
      const arr = divGroups.get(k) ?? [];
      arr.push(t);
      divGroups.set(k, arr);
    }
    reps = [];
    for (const [, divTeams] of divGroups)
    {
      if (divTeams.length === 1)
      {
        reps.push(divTeams[0]);
      }
      else
      {
        reps.push(pickWorstSameDivision(divTeams, ctx));
      }
    }
    if (reps.length === 1)
    {
      return reps[0];
    }
  }
  else
  {
    reps = teams;
  }

  // All reps in the same division (rare after the above reduction).
  if (new Set(reps.map(divKey)).size === 1)
  {
    return pickWorstSameDivision(reps, ctx);
  }

  // All reps in the same conference, different divisions.
  if (reps.every(t => t.conference === reps[0].conference))
  {
    return pickWorstSameConference(reps, ctx);
  }

  // Reps span both conferences.
  return pickWorstCrossConference(reps, ctx);
}

/**
 * Same-division draft-order tiebreaker. Mirrors the NFL division standings
 * ladder with worst-of semantics. Per V1: common games uses the 4-game minimum
 * even within a division.
 */
function pickWorstSameDivision(
  teams: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  if (teams.length === 1)
  {
    return teams[0];
  }

  // 1. Head-to-head among the tied group.
  let group = worstByH2hPct(teams, ctx.games);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.HTH);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameDivision(group, ctx);
  }

  // 2. Best won-lost-tied percentage in division games.
  group = worstOf(teams, t => t.records.division.pctNaN);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.DivPCT);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameDivision(group, ctx);
  }

  // 3. Common-games winning percentage (4-opponent minimum).
  const cgGroup = worstByCommonPct(teams, ctx.games, 4);
  if (cgGroup !== null)
  {
    if (cgGroup.length === 1)
    {
      ctx.rankMethods.set(cgGroup[0].teamAbbreviation, RankMethod.CommonGames);
      return cgGroup[0];
    }
    if (cgGroup.length < teams.length)
    {
      return pickWorstSameDivision(cgGroup, ctx);
    }
  }

  // 4. Best won-lost-tied percentage in conference games.
  group = worstOf(teams, t => t.records.conference.pctNaN);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.ConfPCT);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameDivision(group, ctx);
  }

  // 5. Strength of victory.
  group = worstOf(teams, t => t.sov);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.SOV);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameDivision(group, ctx);
  }

  // 6. Strength of schedule (rarely fires here — already tried at top level).
  group = worstOf(teams, t => t.sos);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.SOS);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameDivision(group, ctx);
  }

  // 7. Coin flip.
  return coinFlipForDraft(teams, ctx);
}

/**
 * Same-conference, different-division draft-order tiebreaker.
 * Mirrors NFL_CONFERENCE_STEPS with worst-of semantics.
 */
function pickWorstSameConference(
  teams: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  if (teams.length === 1)
  {
    return teams[0];
  }

  // 1. Head-to-head among the tied group.
  let group = worstByH2hPct(teams, ctx.games);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.HTH);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameConference(group, ctx);
  }

  // 2. Best won-lost-tied percentage in conference games.
  group = worstOf(teams, t => t.records.conference.pctNaN);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.ConfPCT);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameConference(group, ctx);
  }

  // 3. Common-games winning percentage (4-opponent minimum).
  const cgGroup = worstByCommonPct(teams, ctx.games, 4);
  if (cgGroup !== null)
  {
    if (cgGroup.length === 1)
    {
      ctx.rankMethods.set(cgGroup[0].teamAbbreviation, RankMethod.CommonGames);
      return cgGroup[0];
    }
    if (cgGroup.length < teams.length)
    {
      return pickWorstSameConference(cgGroup, ctx);
    }
  }

  // 4. Strength of victory.
  group = worstOf(teams, t => t.sov);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.SOV);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameConference(group, ctx);
  }

  // 5. Strength of schedule (already tried at top level — included for parity).
  group = worstOf(teams, t => t.sos);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.SOS);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstSameConference(group, ctx);
  }

  // 6. Coin flip.
  return coinFlipForDraft(teams, ctx);
}

/**
 * Cross-conference draft-order tiebreaker. NFL rules name SOS, combined SOS,
 * and coin flip; combined SOS is not modeled by this app, so we follow the V1
 * deviation of including H2H, common games (4-min), and SOV before coin flip.
 * SOS already failed at the top level.
 */
function pickWorstCrossConference(
  teams: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  if (teams.length === 1)
  {
    return teams[0];
  }

  // 1. Head-to-head among the tied group (often a no-op cross-conference).
  let group = worstByH2hPct(teams, ctx.games);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.HTH);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstCrossConference(group, ctx);
  }

  // 2. Common-games winning percentage (4-opponent minimum).
  const cgGroup = worstByCommonPct(teams, ctx.games, 4);
  if (cgGroup !== null)
  {
    if (cgGroup.length === 1)
    {
      ctx.rankMethods.set(cgGroup[0].teamAbbreviation, RankMethod.CommonGames);
      return cgGroup[0];
    }
    if (cgGroup.length < teams.length)
    {
      return pickWorstCrossConference(cgGroup, ctx);
    }
  }

  // 3. Strength of victory.
  group = worstOf(teams, t => t.sov);
  if (group.length === 1)
  {
    ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.SOV);
    return group[0];
  }
  if (group.length < teams.length)
  {
    return pickWorstCrossConference(group, ctx);
  }

  // 4. Coin flip.
  return coinFlipForDraft(teams, ctx);
}

/** Worst-of helper for H2H win pct among a tied group. */
function worstByH2hPct(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
): NflTiebreakerTeam[] {
  const records = computeHthRecords(teams, games);
  return worstOf(teams, t => hthWinPct(records, t.teamAbbreviation));
}

/**
 * Worst-of helper for common-games win pct. Returns null when the tied group
 * shares fewer than `minOpponents` common opponents (step is skipped).
 */
function worstByCommonPct(
  teams: NflTiebreakerTeam[],
  games: GameResultsLookup,
  minOpponents: number,
): NflTiebreakerTeam[] | null {
  const opponentSets = teams.map(t =>
  {
    const teamGames = games.getGamesForTeam(t.teamAbbreviation);
    const opponents = new Set<string>();
    for (const g of teamGames)
    {
      if (g.winner === null)
      {
        continue;
      }
      const opp = g.homeTeam === t.teamAbbreviation ? g.awayTeam : g.homeTeam;
      if (!teams.some(tt => tt.teamAbbreviation === opp))
      {
        opponents.add(opp);
      }
    }
    return opponents;
  });

  let common = new Set(opponentSets[0]);
  for (let i = 1; i < opponentSets.length; i++)
  {
    common = new Set([...common].filter(o => opponentSets[i].has(o)));
  }

  if (common.size < minOpponents)
  {
    return null;
  }

  return worstOf(teams, t =>
  {
    let w = 0, l = 0, ties = 0;
    for (const opp of common)
    {
      const matchups = games.getGamesBetween(t.teamAbbreviation, opp);
      for (const g of matchups)
      {
        if (g.winner === t.teamAbbreviation)
        {
          w++;
        }
        else if (g.winner === opp)
        {
          l++;
        }
        else if (g.winner === null)
        {
          ties++;
        }
      }
    }
    return calcPctNaN([w, l, ties]);
  });
}

/**
 * Coin-flip resolution. Matches V1 DraftOrderCoinFlip semantics: every team in
 * the unresolvable group is labeled CoinFlip, and the lucky one is returned.
 */
function coinFlipForDraft(
  teams: NflTiebreakerTeam[],
  ctx: StepContext<NflTiebreakerTeam>,
): NflTiebreakerTeam {
  const lucky = ctx.coinFlipFn(teams);
  for (const t of teams)
  {
    ctx.rankMethods.set(t.teamAbbreviation, RankMethod.CoinFlip);
  }
  return lucky;
}
